import React, { useContext, useEffect, useRef, useState } from "react";
import DeleteIcon from "../../../../components/VectorIcons/DeleteIcon";
import ThesaurusScope from "../../../../components/ThesaurusScope/ThesaurusScope";
import ThesaurusSelect from "../../../../components/ThesaurusSelect/ThesaurusSelect";
import AlertBox from "../../../../components/AlertBox/AlertBox";
import SmallLoading from "../../../../components/SmallLoading/SmallLoading";
import { AiButton } from "../../../../components/AI/Buttons/AiButton/AiButton";
import { RecommendationsAPIContext } from "../../../../context/RecommendationsAPI/recommendationsAPIContext";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { Box } from "@mui/material";
import { useSheetAttachments } from "../../../../hooks/useSheetAttachments";

const PlotAndThematicAndUniverse = (props) => {
  const {
    options,
    handleAddItem,
    handleUpdateItem,
    defaultValueTertiary,
    defaultValueSecundaries,
    defaultValuePrimaries,
    thesaurusName,
    keyName,
    origin,
    activeTab,
    handleDescriptionChange,
    handleRemoveItem,
    data,
    isProcessingByAI,
    workflowNoContent = false,
  } = props;

  const [focusedIndex, setFocusedIndex] = useState(null);
  const [focusedSelectIndex, setFocusedSelectIndex] = useState(null);
  const [inputHeights, setInputHeights] = useState({
    primaries: {},
    secundaries: {},
    tertiary: {},
  });

  const { setStartRecommendationFlow, hasRunningAIProcess } = useContext(
    RecommendationsAPIContext
  );
  const { attachments, loadingAttachments } = useSheetAttachments({
    contentId: data?.id,
    scope: null,
  });

  const haveDataFilled = Boolean(
    data?.[keyName] &&
    Object.values(data[keyName]).some(
      (item) => Array.isArray(item) && item.length > 0
    )
  );

  const ALLOWED_TYPES_FOR_RECOMMENDATION = ["PLOT"];

  const {
    enabled: AI_FEATURE_IS_ENABLED,
    loading: AI_FEATURE_IS_ENABLED_LOADING,
  } = useFeatureFlag("AI_PLOT_RECOMMENDATION");

  const fieldRefs = useRef({});

  const adjustHeight = (input, relevanceKey, idx) => {
    if (!input) return;

    input.style.height = "auto";
    const scrollHeight = input.scrollHeight;
    const newHeight = scrollHeight > 48 ? scrollHeight : 48;
    input.style.height = `${newHeight}px`;

    setInputHeights((prev) => ({
      ...prev,
      [relevanceKey]: {
        ...prev[relevanceKey],
        [idx]: newHeight,
      },
    }));
  };

  const handleInputChange = (relevanceKey, idx, value) => {
    handleDescriptionChange(relevanceKey, idx, value);

    const input = fieldRefs.current[`${relevanceKey}-${idx}`];

    if (input) {
      if (input.scrollHeight > 48) {
        adjustHeight(input, relevanceKey, idx);
      } else {
        setInputHeights((prev) => ({
          ...prev,
          [relevanceKey]: {
            ...prev[relevanceKey],
            [idx]: 48,
          },
        }));
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (focusedIndex === null) return;

      const currentRef = fieldRefs.current[`${activeTab}-${focusedIndex}`];

      if (currentRef && !currentRef.contains(event.target)) {
        setFocusedIndex(null);
        setInputHeights((prev) => ({
          ...prev,
          [activeTab]: {
            ...prev[activeTab],
            [focusedIndex]: 48,
          },
        }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [focusedIndex, activeTab]);

  useEffect(() => {
    if (focusedIndex !== null) {
      const input = fieldRefs.current[`${activeTab}-${focusedIndex}`];
      if (input) {
        input.focus();
      }
    }
  }, [focusedIndex, activeTab]);

  const renderSelectedItems = (items, relevanceKey) =>
    items.map((item, idx) => (
      <div
        className="formFieldRow"
        key={`${item.id}-${idx}`}
        ref={(el) => {
          if (!fieldRefs.current[`${relevanceKey}-${idx}`]) {
            fieldRefs.current[`${relevanceKey}-${idx}`] = el;
          }
        }}
      >
        {(focusedIndex === idx || focusedSelectIndex === idx) && (
          <div
            className="deleteIcon"
            onMouseDown={() => handleRemoveItem(relevanceKey, idx)}
          >
            <DeleteIcon size={16} color="#666666" />
          </div>
        )}

        <div
          className={`combinedField
            ${focusedIndex === idx || focusedSelectIndex === idx ? "expanded" : ""}
            ${item.generatedByAI ? "aiGenerated" : ""}
          `}
        >
          <div className="selectWrapper">
            <ThesaurusScope
              thesaurusName={thesaurusName}
              thesaurus={options}
              selecteds={[item]}
              searchable={true}
              showBreadcrumb={true}
              addThesaurus={(items) => {
                const newItem = items[1];
                handleUpdateItem(newItem, idx, relevanceKey);
              }}
              hasHierarchy={true}
              origin={origin}
            >
              <ThesaurusSelect
                value={{ value: item.id, label: item.label }}
                options={options}
                onChange={(selected) => {
                  if (selected) {
                    const updatedItem = {
                      id: selected.value,
                      label: selected.label,
                    };
                    handleUpdateItem(updatedItem, idx, relevanceKey);
                  }
                }}
                placeholder=""
                isClearable={false}
                isMulti={false}
                isDisabled={true}
                onFocus={() => setFocusedSelectIndex(idx)}
                onBlur={() => setFocusedSelectIndex(null)}
              />
            </ThesaurusScope>
          </div>

          <div className="separator" />

          <div className="descriptionField">
            {focusedIndex === idx ? (
              <textarea
                ref={(el) => (fieldRefs.current[`${relevanceKey}-${idx}`] = el)}
                className="descriptionInput"
                type="text"
                placeholder="Descrição"
                value={item.descriptionContext || ""}
                onChange={(e) =>
                  handleInputChange(relevanceKey, idx, e.target.value)
                }
                onFocus={() => {
                  const input = fieldRefs.current[`${relevanceKey}-${idx}`];
                  if (input && input.value.trim() !== "") {
                    adjustHeight(input, relevanceKey, idx);
                  }
                }}
                onBlur={() => {
                  setFocusedIndex(null);
                  setInputHeights((prev) => ({
                    ...prev,
                    [relevanceKey]: {
                      ...prev[relevanceKey],
                      [idx]: 48,
                    },
                  }));
                }}
                style={{
                  height:
                    inputHeights[relevanceKey] &&
                    inputHeights[relevanceKey][idx]
                      ? `${inputHeights[relevanceKey][idx]}px`
                      : "48px",
                }}
              />
            ) : (
              <div
                className={`descriptionPreview ${
                  !item.descriptionContext ? "placeholderText" : ""
                }`}
                onClick={() => setFocusedIndex(idx)}
                title={item.descriptionContext || ""}
              >
                <div className="descriptionText">
                  {item.descriptionContext || "Descrição"}
                </div>
                {item.descriptionContext && (
                  <button
                    className="clear-button-preview"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDescriptionChange(relevanceKey, idx, "");
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    ));

  return (
    <div className="formContent">
      {activeTab === "primaries" && (
        <div className="formFieldColumn">
          <div className="formThesaurusScope">
            <div className="selectAiRow">
              <ThesaurusScope
                thesaurusName={thesaurusName}
                thesaurus={options}
                selecteds={[]}
                searchable={true}
                showBreadcrumb={true}
                addThesaurus={(item) =>
                  handleAddItem(item, keyName, "primaries")
                }
                hasHierarchy={true}
                origin={origin}
              >
                <ThesaurusSelect
                  value={null}
                  options={options}
                  onChange={(item) =>
                    handleAddItem([item], keyName, "primaries")
                  }
                  placeholder="Digite um nome e aperte ENTER"
                  noOptionsMessage="Nenhum item encontrado"
                />
              </ThesaurusScope>

              {AI_FEATURE_IS_ENABLED_LOADING && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                  }}
                >
                  <SmallLoading />
                </Box>
              )}

              {!loadingAttachments &&
                AI_FEATURE_IS_ENABLED &&
                !AI_FEATURE_IS_ENABLED_LOADING &&
                ALLOWED_TYPES_FOR_RECOMMENDATION.includes(
                  keyName?.toUpperCase()
                ) && (
                  <AiButton
                    disabled={hasRunningAIProcess}
                    onClick={() =>
                      setStartRecommendationFlow({
                        type: keyName.toUpperCase(),
                        data: {
                          attachments: attachments.items,
                          haveDataFilled,
                          contentId: parseInt(data?.id),
                        },
                      })
                    }
                  />
                )}
            </div>
          </div>

          {isProcessingByAI && !workflowNoContent && (
            <AlertBox
              color="#007EB5"
              type="info"
              text="Exibindo o resultado das tramas (central, secundária e terciária) geradas por IA."
              hasCloseIcon={false}
            />
          )}

          {!!workflowNoContent ? (
            <div className="editRelevanceModal emptyState">
              <Box
                sx={{
                  width: "100%",
                  padding: "32px",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#EC1045",
                  }}
                >
                  Não foi possível identificar tramas no arquivo
                  selecionado.Verifique-o e tente novamente. Caso o problema
                  persista, entre em contato com o suporte.
                </p>
              </Box>
            </div>
          ) : (
            renderSelectedItems(defaultValuePrimaries, "primaries")
          )}
        </div>
      )}

      {activeTab === "secundaries" && (
        <div className="formFieldColumn">
          <div className="formThesaurusScope">
            <ThesaurusScope
              thesaurusName={thesaurusName}
              thesaurus={options}
              selecteds={[]}
              searchable={true}
              showBreadcrumb={true}
              addThesaurus={(item) =>
                handleAddItem(item, keyName, "secundaries")
              }
              hasHierarchy={true}
              origin={origin}
            >
              <ThesaurusSelect
                value={null}
                options={options}
                onChange={(item) =>
                  handleAddItem([item], keyName, "secundaries")
                }
                placeholder="Digite um nome e aperte ENTER"
                noOptionsMessage="Nenhum item encontrado"
              />
            </ThesaurusScope>
          </div>

          {isProcessingByAI && !workflowNoContent && (
            <AlertBox
              color="#007EB5"
              type="info"
              text="Exibindo o resultado das tramas (central, secundária e terciária) geradas por IA."
              hasCloseIcon={false}
            />
          )}

          {workflowNoContent ? (
            <div className="editRelevanceModal emptyState">
              <Box
                sx={{
                  width: "100%",
                  padding: "32px",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#EC1045",
                  }}
                >
                  Não foi possível identificar tramas no arquivo
                  selecionado.Verifique-o e tente novamente. Caso o problema
                  persista, entre em contato com o suporte.
                </p>
              </Box>
            </div>
          ) : (
            renderSelectedItems(defaultValueSecundaries, "secundaries")
          )}
        </div>
      )}

      {activeTab === "tertiary" && (
        <div className="formFieldColumn">
          <div className="formThesaurusScope">
            <ThesaurusScope
              thesaurusName={thesaurusName}
              thesaurus={options}
              selecteds={[]}
              searchable={true}
              showBreadcrumb={true}
              addThesaurus={(item) => handleAddItem(item, keyName, "tertiary")}
              hasHierarchy={true}
              origin={origin}
            >
              <ThesaurusSelect
                value={null}
                options={options}
                onChange={(item) => handleAddItem([item], keyName, "tertiary")}
                placeholder="Digite um nome e aperte ENTER"
                noOptionsMessage="Nenhum item encontrado"
              />
            </ThesaurusScope>
          </div>

          {isProcessingByAI && !workflowNoContent && (
            <AlertBox
              color="#007EB5"
              type="info"
              text="Exibindo o resultado das tramas (central, secundária e terciária) geradas por IA."
              hasCloseIcon={false}
            />
          )}

          {workflowNoContent ? (
            <div className="editRelevanceModal emptyState">
              <Box
                sx={{
                  width: "100%",
                  padding: "32px",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#EC1045",
                  }}
                >
                  Não foi possível identificar tramas no arquivo
                  selecionado.Verifique-o e tente novamente. Caso o problema
                  persista, entre em contato com o suporte.
                </p>
              </Box>
            </div>
          ) : (
            renderSelectedItems(defaultValueTertiary, "tertiary")
          )}
        </div>
      )}
    </div>
  );
};

export default PlotAndThematicAndUniverse;
